import { DownOutlined } from '@ant-design/icons';
import { Dropdown, Modal, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useState } from 'react';
import { useRegion } from '../context/RegionContext';
import type { Region } from '../context/AuthContext';

const REGION_META: Record<Region, { name: string; flag: string }> = {
  // Emoji flags render natively on macOS/Linux/iOS/Android and Win11+.
  // Windows 10 falls back to letter pairs; still readable.
  CA: { name: 'Canada', flag: '🇨🇦' },
  US: { name: 'United States', flag: '🇺🇸' },
};

/**
 * AWS-console-style region picker. Sits inline in the header — subtle text
 * with a caret that indicates it's a dropdown, hover reveals a faint highlight.
 * Only renders for users with access to more than one region.
 */
export default function RegionSwitcher() {
  const { region, availableRegions, isMultiRegion, switchStatus, requestSwitch, forceSwitch, cancelSwitch } =
    useRegion();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  if (!isMultiRegion || !region) return null;

  const active = REGION_META[region];
  const isDraining = switchStatus.state === 'draining';
  const modalOpen = switchStatus.state === 'awaiting-confirmation';
  const target = switchStatus.state !== 'idle' ? switchStatus.target : null;

  const menuItems: MenuProps['items'] = availableRegions.map((r) => {
    const meta = REGION_META[r];
    return {
      key: r,
      label: (
        <Space>
          <span style={{ fontSize: 16, lineHeight: 1 }}>{meta.flag}</span>
          <span style={{ fontWeight: 600, width: 24 }}>{r}</span>
          <span>{meta.name}</span>
        </Space>
      ),
    };
  });

  const handleClick: MenuProps['onClick'] = ({ key }) => {
    void requestSwitch(key as Region);
    setOpen(false);
  };

  return (
    <>
      <Dropdown
        menu={{ items: menuItems, onClick: handleClick, selectedKeys: [region] }}
        trigger={['click']}
        disabled={isDraining}
        open={open}
        onOpenChange={setOpen}
        placement="bottomRight"
      >
        <span
          onClick={(e) => e.preventDefault()}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: '#fff',
            padding: '4px 8px',
            borderRadius: 4,
            cursor: isDraining ? 'progress' : 'pointer',
            opacity: isDraining ? 0.7 : 1,
            background: open || hovered ? 'rgba(255,255,255,0.08)' : 'transparent',
            transition: 'background 120ms ease',
            userSelect: 'none',
          }}
          aria-label={`Region: ${active.name}. Click to switch.`}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>
            {active.flag}
          </span>
          <span style={{ fontWeight: 500 }}>{region}</span>
          <DownOutlined
            style={{
              fontSize: 10,
              opacity: 0.7,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 120ms ease',
            }}
          />
          {isDraining && (
            <Typography.Text style={{ color: '#fff', fontSize: 11, marginLeft: 4 }}>
              switching…
            </Typography.Text>
          )}
        </span>
      </Dropdown>

      <Modal
        open={modalOpen}
        title="Requests still pending"
        okText={`Cancel requests & switch to ${target ?? ''}`}
        cancelText="Keep waiting"
        okButtonProps={{ danger: true }}
        onOk={forceSwitch}
        onCancel={cancelSwitch}
        mask={{ closable: false }}
        closable={false}
      >
        {switchStatus.state === 'awaiting-confirmation' && (
          <p>
            {switchStatus.inflight} request{switchStatus.inflight === 1 ? '' : 's'} to{' '}
            <b>{region}</b> {switchStatus.inflight === 1 ? 'is' : 'are'} still running after 5 seconds.
            You can cancel them and switch to <b>{target}</b> now, or wait for them to finish.
          </p>
        )}
      </Modal>
    </>
  );
}
